// Note: Based on CancelableProcessListener and BackgroundAction

import type { AxiosResponse } from 'axios'

import { callUrl } from '@services/common/api'

import { JOB_FINISHED_STATUSES, JOB_STATUS_ERROR, JOB_STATUS_FINISHED } from '../BackgroundAction'
import { createExpenseRecognition, expenseStopRecognition, updateExpenseRecognition } from './api'
import type { ExpenseCreateByUploadPayload, ExpenseUpdatePayload } from './types'

type PromiseResolveFunction = (payload: BackgroundActionResponse) => void
type PromiseRejectFunction = (payload: unknown) => void
type CalculateIntervalFunction = (tries: number, interval: number, delay: number) => number

type CancelPayload = {
  companyId: Company['id']
  invoice: ItemIdType
}

type Response = BackgroundActionResponse<{ id: ItemIdType }>

class BackgroundActionListener {
  private tries = 0
  private interval = 4
  private delay = 4
  private processTimeout: Nullable<number> = null
  private calculateInterval: CalculateIntervalFunction = (_tries: number, _interval: number, _delay: number) => {
    // after 5 tries: [0,1,2,3,4] ~ 1 min
    if (_tries > 4) {
      return 15000 // 15sec
    }
    return (_tries * _interval + _delay) * 1000
  }

  private responseData: Nullable<Response> = null
  private resolve!: PromiseResolveFunction
  private reject!: PromiseRejectFunction

  private cancelPayload: Nullable<CancelPayload> = null

  constructor() {
    // empty constructor
  }

  //* check running background-job's status
  private run = async () => {
    this.tries++

    // always have results at this point - only need for TS
    if (this.responseData) {
      try {
        // url link exists at this point
        const response = await callUrl<Response>({
          url: this.responseData.links.status as string,
          method: 'get',
        })
        this.responseHandler(response)
      } catch (error) {
        this.reject?.(error)
      }
    }
  }

  private responseHandler = (response: AxiosResponse<Response>) => {
    this.responseData = response.data

    // bg job finished
    if (JOB_STATUS_FINISHED === this.responseData.status) {
      this.resolve?.(this.responseData)
    } else if (JOB_STATUS_ERROR === this.responseData.status) {
      // TODO check it - is it contains "message" key with string message?
      this.reject?.(this.responseData.errors)
    } else {
      this.processTimeout = window.setTimeout(
        () => {
          this.run()
        },
        this.calculateInterval(this.tries, this.interval, this.delay)
      )
    }
  }

  //* call backend to start the background-job
  private start = <Payload>(
    api: (companyId: Company['id'], payload: Payload) => Promise<AxiosResponse<Response>>,
    companyId: Company['id'],
    payload: Payload
  ) => {
    this.tries = 0

    return new Promise((pResolve, pReject) => {
      this.resolve = pResolve
      this.reject = pReject

      api(companyId, payload)
        .then(response => {
          // cancel only available when job is not finished yet
          if (!JOB_FINISHED_STATUSES.includes(response.data.status)) {
            this.cancelPayload = {
              companyId,
              invoice: response.data.results.id,
            }
          }

          this.responseHandler(response)
        })
        .catch(error => {
          this.reject?.(error)
        })
    })
  }

  startCreate = (companyId: Company['id'], payload: ExpenseCreateByUploadPayload) => {
    return this.start(createExpenseRecognition, companyId, payload)
  }

  startUpdate = (companyId: Company['id'], payload: ExpenseUpdatePayload) => {
    return this.start(updateExpenseRecognition, companyId, payload)
  }

  stop = () => {
    if (this.processTimeout) {
      window.clearTimeout(this.processTimeout)
    }
  }

  cancel = async () => {
    this.stop()

    if (this.cancelPayload) {
      try {
        await expenseStopRecognition(this.cancelPayload)
        // fake response for create path
        const responseData = {
          results: { id: this.cancelPayload.invoice },
        } as Response
        this.resolve(responseData)
      } catch (error) {
        this.reject?.(error)
      }
    }
  }
}

export const ExpenseRecognitionBackgroundActionListener = new BackgroundActionListener()
